import routes from '../../routes/routes.json';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../pages/ExpensesPage/expensesPage.constants';

export const INVOICE_MISSING_TARGETS = Object.freeze({
  EXPENSES: 'expenses',
  ACCOUNT: 'account',
  NONE: 'none',
  OVERVIEW: 'overview',
});

/**
 * Picks where the "Invoice Missing" modal's OK button should go. The email link carries no expense
 * or transaction id, so every target is a list or a page, never a single record.
 * - Admin with Account registered (company has banking or PSD2): the Account section, even when the
 *   company has Expenses. Account has the full transaction list, so it wins for an Admin.
 * - Expenses on: the Expenses section, for every other user.
 * - Expenses off, non-Admin with Account registered (PSD2 on and an open-banking account): Account.
 * - Expenses off, no Account, Manager/User: nowhere. The Card section cannot open a transaction
 *   from a URL, so the modal just closes and they stay put.
 * - Expenses off, no Account, Admin: the Overview.
 * - Company features not loaded yet: the Overview, which no role gets a 404 on.
 * Kept free of JSX and store imports so it can be unit tested.
 * @param {Object} params
 * @param {boolean} params.isFeaturesLoaded - Company features have arrived; an unloaded flag reads as false.
 * @param {boolean} params.isExpensesDisabled - The `expenses_disabled` company feature.
 * @param {boolean} params.isAdmin - The user is an Admin.
 * @param {boolean} params.isAccountAvailable - The Account route is registered for this user (same rule as Routes.js).
 * @returns {string} One of INVOICE_MISSING_TARGETS.
 * @example
 * getInvoiceMissingTarget({isFeaturesLoaded: true, isExpensesDisabled: false, isAdmin: false, isAccountAvailable: false}); // 'expenses'
 */
export const getInvoiceMissingTarget = ({
  isFeaturesLoaded,
  isExpensesDisabled,
  isAdmin,
  isAccountAvailable,
}) => {
  if (!isFeaturesLoaded) return INVOICE_MISSING_TARGETS.OVERVIEW;
  if (isAdmin && isAccountAvailable) return INVOICE_MISSING_TARGETS.ACCOUNT;
  if (!isExpensesDisabled) return INVOICE_MISSING_TARGETS.EXPENSES;
  if (isAccountAvailable) return INVOICE_MISSING_TARGETS.ACCOUNT;
  return isAdmin ? INVOICE_MISSING_TARGETS.OVERVIEW : INVOICE_MISSING_TARGETS.NONE;
};

/**
 * Builds the Expenses URL listing the user's own expenses that still lack an invoice. It filters on the one
 * document type that means "no document yet": the filter's "Missing invoice" option (`expense_type=null`).
 * Invoice, Receipt and No Receipt are types a user already chose, so they are not "missing".
 * Built from scratch on purpose: the Expenses page copies every URL param into its API query, so
 * the email's `user_id_in` / `company_id` must not be carried over.
 * When the link names one expense, it opens that expense's details panel instead of the list.
 * @param {string} [email] - The current employee's email; omitted when unknown.
 * @param {string} [expenseId] - Id of the expense the link points at, if it carries one.
 * @returns {string} Expenses path with its query string.
 * @example
 * getInvoiceMissingExpensesUrl('a@b.co'); // '/expenses?has_invoice=false&expense_type=null&reported_by=a%40b.co'
 * getInvoiceMissingExpensesUrl('a@b.co', 'e1'); // '/expenses?selectedExpenseId=e1'
 */
export const getInvoiceMissingExpensesUrl = (email, expenseId) => {
  if (expenseId) {
    return `${routes.expensesList}?${new URLSearchParams({[EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID]: expenseId})}`;
  }
  const params = new URLSearchParams({has_invoice: 'false'});
  // A URL can only carry the filter's null value as the string 'null'.
  params.set('expense_type', String(null));
  if (email) {
    params.set('reported_by', email);
  }
  return `${routes.expensesList}?${params.toString()}`;
};
